<template>
    <div class="mx-2 mx-lg-5">
        <div v-if="canEdit" class="mb-1 mt-3">
            <button class="btn btn-primary btn-sm px-3 py-2 d-flex flex-row justify-content-center align-items-center"
                @click="goToEdit">
                <icon icon="Edit"></icon>
                <span class="ms-2 fs-6 text-white fw-bold">{{ $t("utils.editDetails") }}</span>
            </button>
        </div>
        <div class="card mt-3">
            <div class="card-header d-flex justify-content-between align-items-center">
                <h3>{{ $t("headers.guestbookDetail") }}</h3>
                <div class="d-flex align-items-center gap-2">
                    <button class="btn btn-sm btn-secondary me-3 d-flex align-items-center" @click="addRepeatingEvent">
                        <icon icon="Plus"></icon>
                        <span class="ms-2 fs-6 text-white fw-bold">{{ $t("repeatingEvents.addRepeatingEvent") }}</span>
                    </button>
                    <button class="btn btn-sm btn-secondary me-3 d-flex align-items-center" @click="copyRecord">
                        <icon icon="Copy"></icon>
                        <span class="ms-2 fs-6 text-white fw-bold">{{ $t("utils.copy") }}</span>
                    </button>
                </div>
            </div>
            <div class="card-body">
                <div class="row">
                    <div class="col-md-6">
                        <dl class="row mb-0 align-items-center">
                            <dt class="col-sm-4">{{ $t("guestbook.name") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.guest_name }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.company") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.company }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.cardNumber") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.personal_card_number }}</dd>

                            <dt class="col-sm-4">{{ $t("guestbook.cardType") }}</dt>
                            <dd class="col-sm-8">{{ guestbook.personal_card_type ? $t("documentType." +
                                guestbook.personal_card_type) : "-" }}</dd>

                            <dt class="col-sm-4">
                                <span v-if="guestbook.actual_from">
                                    {{ $t("guestbook.from") }}
                                </span>
                                <span v-else>{{ $t("guestbook.expectedFrom") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="guestbook.actual_from">
                                    {{ $d(new Date(guestbook.actual_from), 'short') }}
                                </span>
                                <span v-else-if="guestbook.expected_from">
                                    {{ $d(new Date(guestbook.expected_from), 'short') }}
                                </span>
                                <span v-else>—</span>
                            </dd>

                            <dt class="col-sm-4">
                                <span v-if="guestbook.actual_to">
                                    {{ $t("guestbook.to") }}
                                </span>
                                <span v-else>{{ $t("guestbook.expectedTo") }}</span>
                            </dt>
                            <dd class="col-sm-8">
                                <span v-if="guestbook.actual_to">
                                    {{ $d(new Date(guestbook.actual_to), 'short') }}
                                </span>
                                <span v-else-if="guestbook.expected_to">
                                    {{ $d(new Date(guestbook.expected_to), 'short') }}
                                </span>
                                <span v-else>—</span>
                            </dd>
                        </dl>
                    </div>

                    <div class="col-md-6">
                        <dl class="row mb-0 align-items-center">
                            <dt class="col-sm-4">{{ $t("status.status") }}</dt>
                            <dd class="col-sm-8">
                                <div
                                    class="d-flex flex-column flex-lg-row justify-content-between align-items-center w-75">
                                    <div class="d-flex align-item-center mb-2 mb-md-2">
                                        <icon :icon="guestbook.status_icon" class="status_icon me-1" />
                                        <span>{{ guestbook.status_name ? $t("status.guestbook." + guestbook.status_name)
                                            : "-" }}</span>
                                    </div>

                                    <button
                                        v-if="$store.getters.hasGatehousePerm('change_status_guestbook') && guestbook.status_name !== 'left'"
                                        class="btn btn-primary fw-bold text-white p-2 ms-5 w-100 d-flex align-items-center justify-content-center"
                                        data-bs-toggle="modal" data-bs-target="#statusModal">
                                        <icon icon="EditPen" class="me-1"></icon>
                                        <span>{{ $t("utils.change") }}</span>
                                    </button>
                                </div>
                            </dd>


                            <dt class="col-sm-4 mt-3">{{ $t("guestbook.signatureArrival") }}</dt>
                            <dd class="col-sm-8 mt-3">
                                <div v-if="!signatureImages.arrival">
                                    <button v-if="$store.getters.hasGatehousePerm('add_signature')" type="button"
                                        class="btn btn-primary fw-bold text-white p-2 me-3 w-75" data-bs-toggle="modal"
                                        data-bs-target="#signatureModal" @click="openSignatureModal('arrival')">
                                        <icon icon="Writing" class="me-1"></icon>
                                        {{ $t("utils.add") }}
                                    </button>
                                    <div v-else class="text-muted">
                                        {{ $t("guestbook.noSignature") }}
                                    </div>
                                </div>
                                <div v-else>
                                    <button type="button" class="btn btn-primary fw-bold text-white p-2 me-3 w-75"
                                        data-bs-toggle="modal" data-bs-target="#viewModal"
                                        @click="openViewModal('arrival')">
                                        <icon icon="Eye" class="me-1"></icon>
                                        {{ $t("utils.view") }}
                                    </button>
                                </div>
                            </dd>

                            <dt class="col-sm-4 mt-3">{{ $t("guestbook.signatureDeparture") }}</dt>
                            <dd class="col-sm-8 mt-3">
                                <div v-if="!signatureImages.departure">
                                    <button v-if="$store.getters.hasGatehousePerm('add_signature')" type="button"
                                        class="btn btn-primary fw-bold text-white p-2 me-3 w-75" data-bs-toggle="modal"
                                        data-bs-target="#signatureModal" @click="openSignatureModal('departure')">
                                        <icon icon="Writing" class="me-1"></icon>
                                        {{ $t("utils.add") }}
                                    </button>
                                    <div v-else class="text-muted">
                                        {{ $t("guestbook.noSignature") }}
                                    </div>
                                </div>
                                <div v-else>
                                    <button type="button" class="btn btn-primary fw-bold text-white p-2 me-3 w-75"
                                        data-bs-toggle="modal" data-bs-target="#viewModal"
                                        @click="openViewModal('arrival')">
                                        <icon icon="Eye" class="me-1"></icon>
                                        {{ $t("utils.view") }}
                                    </button>
                                </div>
                            </dd>
                        </dl>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal for adding signatures -->
    <div class="modal fade" id="signatureModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto">
                    <canvas ref="canvas" class="canvas" width="400" height="200"></canvas>
                </div>
                <div class="modal-footer border-top-0 mb-2">
                    <div class="d-flex justify-content-center gap-4 w-100">
                        <button type="button" class="btn btn-danger" @click="clearCanvas">{{ $t("utils.delete")
                            }}</button>
                        <button type="button" class="btn btn-primary" data-bs-dismiss="modal" @click="saveCanvas">{{
                            $t("utils.save") }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Modal for signature view -->
    <div class="modal fade" id="viewModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content modal_wrapper">
                <div class="modal-header justify-content-between align-items-center border-0 mx-4">
                    <h3 class="modal-title">{{ modalTitle }}</h3>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body mx-auto">
                    <img v-if="signatureType && signatureImages[signatureType]" :src="getSignature" alt="signature" />
                </div>
            </div>
        </div>
    </div>
    <GuestbookStatusesModal :guestbook="guestbook" />
</template>

<script>
import Icon from '@/components/Icon.vue';
import GuestbookStatusesModal from '@/components/GuestbookStatusesModal.vue';
import { CONFIG } from "@/config.js"
import AddRepeatingEvent from './AddRepeatingEvent.vue';

export default {
    components: {
        Icon,
        GuestbookStatusesModal,
    },
    data() {
        return {
            guestbook: {},
            modalTitle: null,
            signatureType: null,
            signatureImages: {
                arrival: null,
                departure: null
            }
        }
    },
    mounted() {
        this.setupCanvas();
    },
    created() {
        this.getGuestbook();
    },
    methods: {
        getGuestbook() {
            this.$store.getters.api.get(`/${this.$route.params.slug}/guestbooks/${this.$route.params.id}`)
                .then(response => {
                    this.guestbook = response.data;

                    const arrivalImg = this.guestbook.signatures.find(sig => sig.is_arrival);
                    const departureImg = this.guestbook.signatures.find(sig => !sig.is_arrival);

                    this.signatureImages.arrival = arrivalImg?.image || null;
                    this.signatureImages.departure = departureImg?.image || null;
                })
                .catch(error => {
                    console.error("Error loading guestbook", error);
                });
        },
        goToEdit() {
            this.$router.push({
                name: "guestbookEditView",
                params: {
                    slug: this.$route.params.slug,
                    id: this.guestbook.id,
                }
            })
        },

        openSignatureModal(type) {
            this.signatureType = type;
            this.modalTitle = this.$t("guestbook.signature." + type)
            this.clearCanvas();
        },
        openViewModal(type) {
            this.signatureType = type;
            this.modalTitle = this.$t("guestbook.signature." + type)
        },
        setupCanvas() {
            this.canvas = this.$refs.canvas
            this.ctx = this.canvas.getContext("2d");

            this.canvas.addEventListener("mousedown", this.startDrawing)
            this.canvas.addEventListener("mousemove", this.draw);
            this.canvas.addEventListener("mouseup", this.stopDrawing);
            this.canvas.addEventListener("mouseleave", this.stopDrawing);
        },
        startDrawing(e) {
            this.drawing = true;
            this.ctx.beginPath();
            this.ctx.moveTo(e.offsetX, e.offsetY);
        },
        draw(e) {
            if (!this.drawing) return;
            this.ctx.lineTo(e.offsetX, e.offsetY);
            this.ctx.stroke();
        },
        stopDrawing() {
            this.drawing = false;
        },
        clearCanvas() {
            if (this.ctx) {
                this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
            }
        },
        saveCanvas() {
            const isArrival = this.signatureType === "arrival";
            const guestbookId = this.guestbook.id;

            this.canvas.toBlob((blob) => {
                if (!blob) return;

                const filename = `${guestbookId}_${this.signatureType}.png`;
                const file = new File([blob], filename, { type: "image/png" });

                const formData = new FormData();
                formData.append("guestbook", guestbookId);
                formData.append("is_arrival", isArrival);
                formData.append("file", file);

                this.$store.getters.api.post(`/${this.$route.params.slug}/signatures/add`, formData)
                    .then(() => {
                        this.getGuestbook();
                    })
                    .catch(error => {
                        console.error("Error adding signature", error);
                    })
            }, "image/png");
        },

        copyRecord() {
            this.$store.commit("setCopyRecord", {
                guestbook: this.guestbook
            });
            this.$router.push({
                name: "guestbookAddView",
                params: {
                    slug: this.$route.params.slug
                }
            })
        },
        addRepeatingEvent() {
            this.$router.push({
                name: "AddRepeatingEvent_Guestbook",
                params: {
                    slug: this.$route.params.slug,
                    id: this.guestbook.id,
                }
            });
        }
    },
    computed: {
        getSignature() {
            const slug = this.$route.params.slug;
            const guestbookId = this.guestbook.id;
            const type = this.signatureType;
            const timestamp = new Date().getTime();
            var url = CONFIG.api.baseUrl;
            url += `/${slug}/guestbooks/${guestbookId}/signatures/file?type=${type}&_=${timestamp}`;
            return url
        },
        canEdit() {
            const status = this.guestbook.status_name;
            const canChange = this.$store.getters.hasGatehousePerm("change_guestbook");
            const canBeforeChange = this.$store.getters.hasGatehousePerm("before_change_guestbook");

            if (status === "left" || status === "arrived") {
                return canChange;
            }

            if (status === "reserved") {
                return canBeforeChange || canChange;
            }

            return false;
        },
    }
}
</script>

<style scoped>
.canvas {
    border: 1px solid #777070;
    background-color: white;
}

.status_icon {
    width: 25px;
    height: 25px;
}
</style>